import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTelegram } from './useTelegram'

/**
 * Root routes that should NOT show the back button
 */
const ROOT_ROUTES = ['/', '/traffic', '/statistics', '/profile', '/resources']

interface UseBackButtonOptions {
  /**
   * Whether to enable the back button management
   * @default true (auto-detect based on current route)
   */
  enabled?: boolean

  /**
   * Custom back button click handler
   * @default Auto-detect parent route
   */
  onBack?: () => void
}

/**
 * Get the parent route for a given path
 */
function getParentRoute(pathname: string): string {
  // Remove trailing slash
  const cleanPath = pathname.endsWith('/') && pathname !== '/'
    ? pathname.slice(0, -1)
    : pathname

  // Split path into segments
  const segments = cleanPath.split('/').filter(Boolean)

  // If already at root, return current path
  if (segments.length === 0) {
    return '/'
  }

  // If at a detail/nested page, determine parent
  if (segments.length === 1) {
    // Single segment like /traffic -> go to root
    return '/'
  }

  // For paths like /bots/123 -> go to /
  if (segments[0] === 'bots' && segments.length === 2) {
    return '/'
  }

  // For paths like /bots/123/auto-resources -> go to /bots/123
  if (segments[0] === 'bots' && segments.length === 3) {
    return `/bots/${segments[1]}`
  }

  // For paths like /traffic/123 -> go to /traffic
  if (segments[0] === 'traffic' && segments.length === 2) {
    return '/traffic'
  }

  // For paths like /traffic/123/price -> go to /traffic/123
  if (segments[0] === 'traffic' && segments.length === 3) {
    return `/traffic/${segments[1]}`
  }

  // Default: go up one level
  segments.pop()
  return segments.length > 0 ? `/${segments.join('/')}` : '/'
}

/**
 * Automatically manages Telegram's BackButton based on the current route
 *
 * Features:
 * - Auto-hides on root routes (/, /traffic, /statistics, /profile)
 * - Auto-shows on nested routes
 * - Intelligently navigates to parent route
 * - Supports custom onBack handler
 * - Proper cleanup on unmount
 *
 * @example
 * // Auto-manage (recommended for most pages)
 * useBackButton()
 *
 * @example
 * // Custom back handler
 * useBackButton({ onBack: () => navigate('/custom-path') })
 *
 * @example
 * // Disable for a specific page
 * useBackButton({ enabled: false })
 */
export function useBackButton(options: UseBackButtonOptions = {}) {
  const { enabled = true, onBack } = options
  const navigate = useNavigate()
  const location = useLocation()
  const { showBackButton, hideBackButton } = useTelegram()

  useEffect(() => {
    if (!enabled) {
      hideBackButton()
      return
    }

    // Check if current route is a root route
    const isRootRoute = ROOT_ROUTES.includes(location.pathname)

    if (isRootRoute) {
      // Hide back button on root routes
      hideBackButton()
    } else {
      // Show back button on nested routes
      const handleBack = () => {
        // Scroll to top before navigation
        window.scrollTo(0, 0)

        if (onBack) {
          // Use custom handler if provided
          onBack()
        } else {
          // Default: navigate to parent route
          const parentRoute = getParentRoute(location.pathname)
          navigate(parentRoute)
        }
      }

      showBackButton(handleBack)
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      hideBackButton()
    }
  }, [enabled, location.pathname, onBack, navigate, showBackButton, hideBackButton])
}
